/**
 * CreatePlanPage — formulario de generación de plan (Sprint 6 Bloque 4).
 *
 * Inputs:
 * - race_distance (select con todas las opciones del enum del backend).
 * - race_date (input type=date).
 *
 * Validaciones cliente:
 * - race_distance requerido.
 * - race_date requerida.
 * - race_date estrictamente futura (> hoy).
 * - race_date <= hoy + 365 días (max 1 año por delante).
 *
 * Errores 400 del backend:
 * - profile_incomplete → toast con copy específico (decisión técnica 3
 *   Bloque 4 — la ruta /onboarding aún no existe; mostramos toast
 *   provisional). Permanece en la página para que el user lo lea.
 * - race_date_in_past_or_today / race_date_too_far / race_date_invalid /
 *   race_distance_required → reutilizamos copy genérico de error.
 *
 * Submit OK (201) → redirige a /plan. La PlanPage hace fetch fresh.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { getOnboardingStatus } from "@/api/onboarding";
import { createPlan } from "@/api/training";
import { t } from "@/i18n/es";
import { getAllowedRaceDistances } from "@/lib/raceDistance";
import { ApiError, type AthleteLevel } from "@/types/api";

interface Toast {
  id: number;
  message: string;
  kind: "success" | "error";
}

let toastSeq = 0;

const RACE_DISTANCE_OPTIONS: ReadonlyArray<{ value: string; labelKey: keyof typeof t.plan }> = [
  { value: "5K", labelKey: "raceDistance5k" },
  { value: "10K", labelKey: "raceDistance10k" },
  { value: "15K", labelKey: "raceDistance15k" },
  { value: "20K", labelKey: "raceDistance20k" },
  { value: "half_marathon", labelKey: "raceDistanceHalfMarathon" },
  { value: "marathon", labelKey: "raceDistanceMarathon" },
  { value: "trail_short", labelKey: "raceDistanceTrailShort" },
  { value: "trail_long", labelKey: "raceDistanceTrailLong" },
  // Sprint 13 S13.6a: `personal_challenge` se elimina de la rama carrera —
  // "sin carrera" ahora es el modo Plan de Progresión (abajo).
];

/** Sprint 13 S13.6a (motor v10 §2): distancias del Plan de Progresión.
 *  CRÍTICO: valores en MINÚSCULA (`5k`/`10k`/`15k`) para coincidir con
 *  `PROGRESSION_DISTANCES` del backend; la rama carrera usa mayúscula
 *  (`5K`...) que el backend tolera vía parser de fallback, pero la
 *  validación de progresión es estricta y exige minúscula. NO incluye
 *  20K ni trail (v10 §2: 5K/10K/15K/21K/42K). */
const PROGRESSION_DISTANCE_OPTIONS: ReadonlyArray<{ value: string; labelKey: keyof typeof t.plan }> = [
  { value: "5k", labelKey: "raceDistance5k" },
  { value: "10k", labelKey: "raceDistance10k" },
  { value: "15k", labelKey: "raceDistance15k" },
  { value: "half_marathon", labelKey: "raceDistanceHalfMarathon" },
  { value: "marathon", labelKey: "raceDistanceMarathon" },
];

/** Devuelve la fecha de hoy y la de hoy + 365 días en formato ISO `YYYY-MM-DD`
 *  para los atributos `min` / `max` del input date. */
function _datePickerBounds(): { tomorrowIso: string; maxIso: string } {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const max = new Date(today);
  max.setDate(today.getDate() + 365);
  return {
    tomorrowIso: tomorrow.toISOString().slice(0, 10),
    maxIso: max.toISOString().slice(0, 10),
  };
}

/** Sprint 11 S11.1 (motor v8 §1): bounds del date picker de `start_date`.
 *  Backend acepta [today, today+30d]. UI lo restringe igualmente. */
function _startDatePickerBounds(): { todayIso: string; max30Iso: string } {
  const today = new Date();
  const max = new Date(today);
  max.setDate(today.getDate() + 30);
  return {
    todayIso: today.toISOString().slice(0, 10),
    max30Iso: max.toISOString().slice(0, 10),
  };
}

/** Construye un string ISO YYYY-MM-DD desde una Date interpretando sus
 *  componentes LOCALES (no UTC). Evita el drift de 1 día que produce
 *  `Date.toISOString().slice(0,10)` cuando el local TZ tiene offset
 *  positivo y la hora local es de madrugada (la versión UTC ya ha
 *  saltado al día siguiente). */
function _toLocalIsoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Próximo lunes (Nexum convention) en formato ISO LOCAL. Sprint 11 S11.1:
 *  default value del date picker de start_date. Mismo cálculo que
 *  `plan_generator._next_monday` en backend para que el UI muestre la
 *  fecha que el server escogería si el atleta no toca el campo.
 *
 *  Usa `_toLocalIsoDate` (no `toISOString`) porque el cálculo aquí es
 *  LOCAL: `getDay()` + `setDate()` operan en local time. Serializar a
 *  UTC introduciría drift de 1 día en TZ con offset positivo. */
function _nextMondayIso(): string {
  const d = new Date();
  const jsDay = d.getDay(); // 0=dom, 1=lun ... 6=sab
  // Si HOY es lunes (jsDay===1) → next_monday = HOY mismo (Nexum convention,
  // mismo que backend _next_monday cuando today ya es lunes).
  const daysUntilMonday = jsDay === 1 ? 0 : (8 - jsDay) % 7;
  d.setDate(d.getDate() + daysUntilMonday);
  return _toLocalIsoDate(d);
}

function _validate(
  raceDistance: string,
  raceDate: string,
  startDate: string,
): string | null {
  if (!raceDistance) return t.plan.errorRaceDistanceRequired;
  if (!raceDate) return t.plan.errorRaceDateRequired;
  const todayIso = new Date().toISOString().slice(0, 10);
  if (raceDate <= todayIso) return t.plan.errorRaceDateInPast;
  // hoy + 365 inclusive.
  const limit = new Date();
  limit.setDate(limit.getDate() + 365);
  const limitIso = limit.toISOString().slice(0, 10);
  if (raceDate > limitIso) return t.plan.errorRaceDateTooFar;
  // Sprint 11 S11.1: validación start_date.
  if (startDate) {
    if (startDate < todayIso) return t.plan.startDateStepErrorBeforeToday;
    const max30 = new Date();
    max30.setDate(max30.getDate() + 30);
    const max30Iso = max30.toISOString().slice(0, 10);
    if (startDate > max30Iso) return t.plan.startDateStepErrorAfter30Days;
    if (startDate >= raceDate) return t.plan.startDateStepErrorAfterRaceDate;
  }
  return null;
}

export function CreatePlanPage() {
  const navigate = useNavigate();
  // Sprint 13 S13.6a (motor v10 §2): modo de plan. 'race' = flujo actual
  // (carrera + fecha). 'progression' = Plan de Progresión sin race_date.
  const [planMode, setPlanMode] = useState<"race" | "progression">("race");
  const [raceDistance, setRaceDistance] = useState("");
  // Distancia del Plan de Progresión (valores en minúscula — ver
  // PROGRESSION_DISTANCE_OPTIONS). Estado separado para no mezclar con
  // los valores en mayúscula de la rama carrera.
  const [progressionDistance, setProgressionDistance] = useState("");
  const [raceDate, setRaceDate] = useState("");
  // Sprint 11 S11.1 (motor v8 §1): default = próximo lunes (mismo cálculo
  // que backend `_next_monday(today)`). El atleta puede cambiarlo al
  // rango [today, today+30d].
  const [startDate, setStartDate] = useState<string>(_nextMondayIso());
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  // v15.5-§8.4: nivel del atleta para filtrar el dropdown de distancias.
  // null = aún no fetcheado o atleta sin perfil. El filtro trata null
  // como "sin restricción" (defensa — otros gates handle profile_incomplete).
  const [athleteLevel, setAthleteLevel] = useState<AthleteLevel | null>(null);

  // v15.5-§8.4: fetch onboarding al montar para conocer level del atleta.
  // Fire-and-forget: si falla, el filtro queda en null y se muestran
  // todas las opciones (defensa); el backend bloquearía cualquier
  // combinación inválida con código `beginner_max_5k`.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await getOnboardingStatus();
        if (!cancelled) {
          setAthleteLevel(status.profile?.level ?? null);
        }
      } catch {
        // ignore — defensa backend cubre.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // v15.5-§8.4: listas filtradas según level. Beginner solo ve "5K"/"5k";
  // amateur y otros niveles ven todo. Se computa en render — el coste es
  // O(n) sobre ≤8 elementos.
  const filteredRaceOptions = RACE_DISTANCE_OPTIONS.filter((opt) =>
    getAllowedRaceDistances(
      athleteLevel,
      RACE_DISTANCE_OPTIONS.map((o) => o.value),
    ).includes(opt.value),
  );
  const filteredProgressionOptions = PROGRESSION_DISTANCE_OPTIONS.filter((opt) =>
    getAllowedRaceDistances(
      athleteLevel,
      PROGRESSION_DISTANCE_OPTIONS.map((o) => o.value),
    ).includes(opt.value),
  );

  const { tomorrowIso, maxIso } = _datePickerBounds();
  const { todayIso: startTodayIso, max30Iso: startMax30Iso } = _startDatePickerBounds();

  const showToast = useCallback(
    (message: string, kind: "success" | "error" = "success") => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((tt) => tt.id !== id));
      }, 5000);
    },
    [],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Sprint 13 S13.6a: validación + payload según el modo.
    if (planMode === "progression") {
      if (!progressionDistance) {
        setFieldError(t.plan.errorRaceDistanceRequired);
        return;
      }
    } else {
      const validationError = _validate(raceDistance, raceDate, startDate);
      if (validationError) {
        setFieldError(validationError);
        return;
      }
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      if (planMode === "progression") {
        // Plan de Progresión: solo distancia, SIN race_date (el backend
        // sintetiza race_date = start + 12 sem).
        await createPlan({
          plan_type: "progression",
          race_distance: progressionDistance,
        });
      } else {
        await createPlan({
          race_distance: raceDistance,
          race_date: raceDate,
          // Sprint 11 S11.1: incluir start_date en el payload. Si el
          // atleta NO tocó el campo, va el default next_monday calculado
          // local (mismo valor que el backend produciría sin el campo,
          // así que el comportamiento es idéntico).
          start_date: startDate,
        });
      }
      showToast(t.plan.toastPlanCreated, "success");
      // Pequeña espera para que el toast sea visible antes de redirigir.
      // 700ms es suficiente para registrar el feedback sin cansar.
      setTimeout(() => navigate("/plan"), 700);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "profile_incomplete") {
          // Sprint 6 Bloque 5: ahora SÍ tenemos /onboarding. Redirigir
          // al usuario a completar/actualizar el perfil. El wizard
          // sobreescribe los datos y vuelve a /home; el user puede
          // navegar de nuevo a /plan/crear cuando esté listo.
          showToast(t.plan.toastProfileIncomplete, "error");
          setSubmitting(false);
          setTimeout(() => navigate("/onboarding"), 800);
          return;
        } else if (err.code === "race_date_in_past_or_today") {
          setFieldError(t.plan.errorRaceDateInPast);
        } else if (err.code === "race_date_too_far") {
          setFieldError(t.plan.errorRaceDateTooFar);
        } else if (err.code === "race_distance_required") {
          setFieldError(t.plan.errorRaceDistanceRequired);
        } else if (err.code === "invalid_progression_distance") {
          // Sprint 13 S13.6a: distancia de progresión fuera del enum.
          setFieldError(t.plan.errorRaceDistanceRequired);
        } else if (err.code === "beginner_max_5k") {
          // v15.5-§8.4: defensa backend disparada. El filtro frontend
          // debería haber prevenido el caso; si llegamos aquí es
          // desincronización (level cambió entre fetch y submit).
          setFieldError(t.plan.errorBeginnerMax5k);
        } else if (err.code === "race_date_invalid") {
          setFieldError(t.plan.errorRaceDateRequired);
        // Sprint 11 S11.1: errores específicos de start_date.
        } else if (err.code === "start_date_in_past") {
          setFieldError(t.plan.startDateStepErrorBeforeToday);
        } else if (err.code === "start_date_too_far") {
          setFieldError(t.plan.startDateStepErrorAfter30Days);
        } else if (err.code === "start_date_after_race_date") {
          setFieldError(t.plan.startDateStepErrorAfterRaceDate);
        } else if (err.code === "start_date_invalid") {
          setFieldError(t.plan.startDateStepErrorBeforeToday);
        } else {
          showToast(t.plan.toastError, "error");
        }
      } else {
        showToast(t.plan.toastError, "error");
      }
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone">
      <header>
        <Link to="/plan" className="text-xs text-bone-muted hover:text-bone">
          ← {t.plan.pageTitle}
        </Link>
        <h1 className="mt-4 text-2xl font-medium tracking-tight">
          {t.plan.createTitle}
        </h1>
      </header>

      <form
        data-testid="create-plan-form"
        onSubmit={(e) => void onSubmit(e)}
        className="flex flex-col gap-4"
        noValidate
      >
        {/* Sprint 13 S13.6a (motor v10 §2): selector de modo de plan.
            'race' = carrera objetivo (flujo actual). 'progression' = Plan
            de Progresión sin carrera. */}
        <fieldset
          data-testid="plan-mode-step"
          className="flex flex-col gap-3"
        >
          <legend className="text-xs uppercase tracking-widest text-gray-mid">
            {t.plan.createModeLabel}
          </legend>
          {/* F12 (pre-flight): ayuda siempre visible para que un novato
              entienda qué elegir, antes de seleccionar. */}
          <p
            data-testid="plan-mode-help"
            className="text-xs text-bone-muted"
          >
            {t.plan.createModeHelp}
          </p>
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-subtle p-3 hover:border-border-strong">
            <input
              type="radio"
              name="plan-mode"
              value="race"
              data-testid="plan-mode-race"
              checked={planMode === "race"}
              onChange={() => {
                setPlanMode("race");
                setFieldError(null);
              }}
              className="mt-0.5"
            />
            <span className="text-sm text-bone">{t.plan.createModeRace}</span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-subtle p-3 hover:border-border-strong">
            <input
              type="radio"
              name="plan-mode"
              value="progression"
              data-testid="plan-mode-progression"
              checked={planMode === "progression"}
              onChange={() => {
                setPlanMode("progression");
                setFieldError(null);
              }}
              className="mt-0.5"
            />
            <span className="text-sm text-bone">
              {t.plan.createModeProgression}
            </span>
          </label>
        </fieldset>

        {planMode === "race" ? (
          <>
            <div className="flex flex-col gap-2">
              <label htmlFor="race_distance" className="text-xs uppercase tracking-widest text-gray-mid">
                {t.plan.createRaceDistanceLabel}
              </label>
              <select
                id="race_distance"
                data-testid="race-distance-select"
                value={raceDistance}
                onChange={(e) => setRaceDistance(e.target.value)}
                required
                className="rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
              >
                <option value="">—</option>
                {/* v15.5-§8.4: lista filtrada por level del atleta.
                    Beginner solo ve "5K"; amateur ve todas. */}
                {filteredRaceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t.plan[opt.labelKey]}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="race_date" className="text-xs uppercase tracking-widest text-gray-mid">
                {t.plan.createRaceDateLabel}
              </label>
              <input
                id="race_date"
                data-testid="race-date-input"
                type="date"
                value={raceDate}
                min={tomorrowIso}
                max={maxIso}
                onChange={(e) => setRaceDate(e.target.value)}
                required
                className="rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
              />
            </div>

            {/* Sprint 11 S11.1 (motor v8 §1): step "Fecha de inicio del plan".
                Selector entre today y today+30d. Default próximo lunes.
                Helper text explica el efecto en el TEST-VAM para amateur+watch. */}
            <div
              data-testid="start-date-step"
              className="flex flex-col gap-2"
            >
              <label
                htmlFor="start_date"
                className="text-xs uppercase tracking-widest text-gray-mid"
              >
                {t.plan.startDateStepLabel}
              </label>
              <input
                id="start_date"
                data-testid="start-date-input"
                type="date"
                value={startDate}
                min={startTodayIso}
                max={startMax30Iso}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
              />
              <p className="text-xs text-bone-muted">
                {t.plan.startDateStepDescription}
              </p>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <label
              htmlFor="progression_distance"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.plan.createRaceDistanceLabel}
            </label>
            <select
              id="progression_distance"
              data-testid="progression-distance-select"
              value={progressionDistance}
              onChange={(e) => setProgressionDistance(e.target.value)}
              required
              className="rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            >
              <option value="">—</option>
              {/* v15.5-§8.4: lista filtrada por level del atleta.
                  Beginner solo ve "5k"; amateur ve todas. */}
              {filteredProgressionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t.plan[opt.labelKey]}
                </option>
              ))}
            </select>
            <p
              data-testid="progression-hint"
              className="text-xs text-bone-muted"
            >
              {t.plan.createProgressionHint}
            </p>
          </div>
        )}

        {fieldError && (
          <p
            data-testid="create-plan-error"
            role="alert"
            className="text-sm text-danger"
          >
            {fieldError}
          </p>
        )}

        <button
          type="submit"
          data-testid="create-plan-submit"
          disabled={submitting}
          className="mt-2 self-start rounded-md bg-lime px-4 py-2.5 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t.plan.createSubmit}
        </button>
      </form>

      {/* Toast stack */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          className="pointer-events-none fixed bottom-toast right-4 z-50 flex max-w-xs flex-col gap-2"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              data-testid={`toast-${toast.kind}`}
              className="pointer-events-auto rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone"
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
